import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountDetailsDrawerData,
  AddBucketDrawerData,
  BucketDrawerData,
  BucketDrawerTab,
  GroupsPieChartDrawerData,
  CustomAccountDrawerData,
  DocumentationDrawerData,
  RecurringTransactionDrawerData,
  TransactionRuleDrawerData,
  DrawerService,
  DrawerComponentData,
  UnbudgetedDrawerData,
} from '../../services/drawer.service';
import { NavigationService } from '../../services/navigation.service';
import { BucketDrawerWrapperComponent } from '../drawer-wrappers/bucket-drawer-wrapper/bucket-drawer-wrapper.component';
import { AddBucketDrawerWrapperComponent } from '../drawer-wrappers/add-bucket-drawer-wrapper/add-bucket-drawer-wrapper.component';
import { AccountDetailsDrawerWrapperComponent } from '../drawer-wrappers/account-details-drawer-wrapper/account-details-drawer-wrapper.component';
import { UnbudgetedDrawerWrapperComponent } from '../drawer-wrappers/unbudgeted-drawer-wrapper/unbudgeted-drawer-wrapper.component';
import { CategoriesPieChartDrawerWrapperComponent } from '../drawer-wrappers/categories-pie-chart-drawer-wrapper/categories-pie-chart-drawer-wrapper.component';
import { CustomAccountDrawerWrapperComponent } from '../drawer-wrappers/custom-account-drawer-wrapper/custom-account-drawer-wrapper.component';
import { DocumentationDrawerWrapperComponent } from '../drawer-wrappers/documentation-drawer-wrapper/documentation-drawer-wrapper.component';
import { RecurringTransactionDrawerWrapperComponent } from '../drawer-wrappers/recurring-transaction-drawer-wrapper/recurring-transaction-drawer-wrapper.component';
import { TransactionRuleDrawerWrapperComponent } from '../drawer-wrappers/transaction-rule-drawer-wrapper/transaction-rule-drawer-wrapper.component';

@Component({
  selector: 'app-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drawer.component.html',
})
export class DrawerComponent {
  drawerService = inject(DrawerService);
  private nav = inject(NavigationService);

  private readonly destroyRef = inject(DestroyRef);
  private drawerData: Record<string, (params: Record<string, string | undefined>) => DrawerComponentData> = {
    budget: (params): DrawerComponentData => ({
      type: BucketDrawerWrapperComponent,
      data: {
        bucketId: params.drawerBudgetId,
        tab: params.drawerBudgetTab as BucketDrawerTab | undefined,
      } as BucketDrawerData,
      reuseComponent: true,
    }),
    'new-budget': (params): DrawerComponentData => ({
      type: AddBucketDrawerWrapperComponent,
      data: { groupId: params.drawerGroupId ?? params.drawerCategoryId } as AddBucketDrawerData,
    }),
    'account-details': (params): DrawerComponentData => ({
      type: AccountDetailsDrawerWrapperComponent,
      data: {
        accountId: params.drawerAccountId,
        credentialId: params.drawerCredentialId,
      } as AccountDetailsDrawerData,
    }),
    'categories-chart': (params): DrawerComponentData => ({
      type: CategoriesPieChartDrawerWrapperComponent,
      data: { groupId: params.drawerGroupId ?? params.drawerCategoryId } as GroupsPieChartDrawerData,
    }),
    'custom-account': (params): DrawerComponentData => ({
      type: CustomAccountDrawerWrapperComponent,
      data: { accountId: params.drawerAccountId } as CustomAccountDrawerData,
    }),
    available: (): DrawerComponentData => ({
      type: UnbudgetedDrawerWrapperComponent,
      data: {} as UnbudgetedDrawerData,
    }),
    documentation: (params): DrawerComponentData => ({
      type: DocumentationDrawerWrapperComponent,
      data: { slug: params.drawerSlug } as DocumentationDrawerData,
    }),
    'recurring-transaction': (params): DrawerComponentData => ({
      type: RecurringTransactionDrawerWrapperComponent,
      data: { recurringTransactionId: params.drawerRecurringTransactionId } as RecurringTransactionDrawerData,
    }),
    'transaction-rule': (params): DrawerComponentData => ({
      type: TransactionRuleDrawerWrapperComponent,
      data: {
        ruleId: params.drawerRuleId,
        suggestionId: params.drawerSuggestionId,
        transactionId: params.drawerTransactionId,
      } as TransactionRuleDrawerData,
    }),
  };

  constructor() {
    this.nav.routes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const drawerData = this.getDrawerDataFromParams(this.nav.params);
      if (drawerData) return this.openDrawer(drawerData);

      this.drawerService.navOpen.set(false);
      this.drawerService.closeMobileSheet();
    });
  }

  private getDrawerDataFromParams(params: Record<string, string | undefined>) {
    if (params.drawer === 'budget' && params.drawerBudgetId) {
      const getDrawerData = this.drawerData.budget;
      if (getDrawerData) return getDrawerData(params);
    }
    if (params.drawer === 'account-details' && params.drawerAccountId) {
      const getDrawerData = this.drawerData['account-details'];
      if (getDrawerData) return getDrawerData(params);
    }
    if (params.drawer === 'recurring-transaction' && !params.drawerRecurringTransactionId) return false;
    if (params.drawer) {
      const getDrawerData = this.drawerData[params.drawer];
      if (getDrawerData) return getDrawerData(params);
    }
    return false;
  }

  private openDrawer(data: DrawerComponentData) {
    this.drawerService.setCurrentComponent(data);

    if (window.innerWidth >= 600) {
      this.drawerService.closeMobileSheet();
      this.drawerService.desktopDrawerActive.set(true);
      this.drawerService.navOpen.set(true);
    } else {
      this.drawerService.desktopDrawerActive.set(false);
      this.drawerService.navOpen.set(false);
      this.drawerService.bottomSheetOpen.set(true);
      window.document.body.classList.add('noscroll');
    }
  }
}
